import React, { useState, useEffect } from 'react';
import GlobalCacheCalculator from '../utils/GlobalCacheCalculator';
import './AdminCachePanel.css';

const AdminCachePanel = () => {
  const [calculator] = useState(new GlobalCacheCalculator());
  const [isCalculating, setIsCalculating] = useState(false);
  const [progress, setProgress] = useState({
    categories: 0,
    dancers: 0,
    currentTask: ''
  });
  const [result, setResult] = useState(null);

  useEffect(() => {
    calculator.addProgressListener(setProgress);
  }, [calculator]);

  const startCalculation = async () => {
    setIsCalculating(true);
    setResult(null);
    setProgress({ categories: 0, dancers: 0, currentTask: 'Starting...' });

    try {
      const calculationResult = await calculator.calculateAllRankings();
      setResult(calculationResult);
    } catch (error) {
      setResult({
        success: false,
        error: error.message
      });
    } finally {
      setIsCalculating(false);
    }
  };

  return (
    <div className="admin-cache-panel">
      <div className="cache-panel-header">
        <h2>🚀 Global Cache Calculator</h2>
        <p>Pre-calculate rankings for all dancers to enable lightning-fast profile loads</p>
      </div>

      <div className="cache-controls">
        <button 
          onClick={startCalculation}
          disabled={isCalculating}
          className={`calculate-btn ${isCalculating ? 'calculating' : ''}`}
        >
          {isCalculating ? '⏳ Calculating...' : '🎯 Start Global Calculation'}
        </button>
      </div>

      {isCalculating && (
        <div className="progress-panel">
          <div className="progress-header">
            <h3>📊 Calculation Progress</h3>
          </div>
          
          <div className="progress-stats">
            <div className="stat-item">
              <span className="stat-label">Categories:</span>
              <span className="stat-value">{progress.categories}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Dancers:</span>
              <span className="stat-value">{progress.dancers}</span>
            </div>
          </div>
          
          <div className="current-task">
            <span className="task-label">Current Task:</span>
            <span className="task-value">{progress.currentTask}</span>
          </div>
          
          <div className="progress-animation">
            <div className="spinner"></div>
          </div>
        </div>
      )}

      {result && (
        <div className={`result-panel ${result.success ? 'success' : 'error'}`}>
          {result.success ? (
            <div className="success-result">
              <h3>✅ Calculation Complete!</h3>
              <div className="result-stats">
                <div className="result-item">
                  <span>⏱️ Duration:</span>
                  <span>{result.duration} seconds</span>
                </div>
                <div className="result-item">
                  <span>📊 Categories:</span>
                  <span>{result.categoriesProcessed}</span>
                </div>
                <div className="result-item">
                  <span>👥 Dancers:</span>
                  <span>{result.dancersProcessed}</span>
                </div>
              </div>
              <div className="success-message">
                🎉 All dancer profiles will now load instantly from cache!
              </div>
            </div>
          ) : (
            <div className="error-result">
              <h3>❌ Calculation Failed</h3>
              <div className="error-message">
                {result.error}
              </div>
              <div className="error-help">
                💡 Try refreshing the page and ensuring you have a stable internet connection.
              </div>
            </div>
          )}
        </div>
      )}

      <div className="cache-info">
        <h3>ℹ️ About Global Cache</h3>
        <ul>
          <li>Pre-calculates rankings for all dancers across all categories</li>
          <li>Enables instant profile loads (100ms vs 5+ seconds)</li>
          <li>Processes 40+ categories and 1000+ dancer profiles</li>
          <li>Should be run daily during active seasons</li>
          <li>Safe to run multiple times - overwrites previous cache</li>
        </ul>
      </div>
    </div>
  );
};

export default AdminCachePanel;
